import React, {useEffect, useMemo, useState} from "react";
import {useAuth} from "./AuthProvider";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import TalentTeam from "../models/TalentTeam";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAPI} from "../config/API";


let TalentTeamContext = React.createContext(null);

const TalentTeamProvider = (props) => {

    const {auth} = useAuth();
    const {teamId, projectId} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isUpdating, setIsUpdating] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [team, setTeam] = useState(props.model ? TalentTeam.make({...(props.model || {})}) : undefined);

    const requestParams = useMemo(() => {
        let rParams = {teamId: parseInt(teamId)};
        if (projectId && !TB.isNaN(projectId) && auth?.isClient()) {
            rParams.projectId = projectId;
        }
        return rParams
    }, [teamId, searchParams?.toString(), auth]);
    const {
        data: teamResponse,
        isFetching,
        error,
        refetch
    } = useAPI(
        ['fetchTeam', requestParams],
        {
            enabled: !props.model && !TB.isNaN(parseInt(teamId)),
        }
    );


    useEffect(() => {
        if ((!props.model && !teamId) || (teamId === 'new' && !team)) {
            setTeam(TalentTeam.make({}));
            setIsInitializing(false)
        }else if (teamResponse && !isFetching && teamId) {
            setTeam(TalentTeam.make({...teamResponse}));
            setIsInitializing(false)
        }else if (!isFetching && teamId && team === undefined && teamResponse === false) {
            setTeam(false);
            setIsInitializing(false)
        }else if (team) {
            setIsInitializing(false)
        }
    }, [teamResponse, isFetching, teamId, props.model]);

    const syncTeamQuery = async(updatedModel) => {
        await setTeam(TalentTeam.make({...updatedModel}))
        return queryClient.setQueryData(
            ['fetchTeam', {teamId:parseInt(teamId)}],
            previousData => previousData ? updatedModel : undefined
        )
    }

    const teamRequestMutation = useMutation(['talentTeamRequest', teamId], (callback) => {
        return callback()
    })

    const teamRequest = async (methodNameOrCallback, params, successMessage = "Team Updated") => {
        await setIsUpdating(true);
        let updatedTeam;

        if (typeof methodNameOrCallback === 'string' && team[methodNameOrCallback]) {
            updatedTeam = await teamRequestMutation.mutateAsync(() => team[methodNameOrCallback](params)).catch(console.warn);
        }else if (typeof methodNameOrCallback === 'function') {
            updatedTeam = await teamRequestMutation.mutateAsync(methodNameOrCallback).catch(console.warn);
        }

        if (!updatedTeam) {
            setIsUpdating(false);
            TB.showServerErrorToast();
            return;
        }
        await syncTeamQuery(updatedTeam)
        setIsUpdating(false)

        if(successMessage) {
            if (TB.has(laravel.alerts, successMessage)) {
                await TB.wait(250);
                return TB.sysAlert(successMessage)
            }else {
                TB.toast(successMessage);
            }
        }
        return updatedTeam;
    }

    const updateTeamDetails = async(teamParams) => {
        if (!teamParams) {
            teamParams = {...team};
        }
        let params = TB.pick(teamParams, ['name', 'bio', 'profile_links', 'banner_image_path', 'is_available']);

        params.is_available = params.is_available ? 1 : 0
        return teamRequest('update', params);
    }
    const createTeam = async(props = {}) => {
        await setIsUpdating(true);

        let updatedTeam;
        try {
            updatedTeam = await TalentTeam.create({...team, ...props});
        }catch (e) {

        }
        if (!updatedTeam) {
            setIsUpdating(false);
            TB.showServerErrorToast();
            return;
        }

        await syncTeamQuery(updatedTeam)

        setIsUpdating(false)

        return updatedTeam;
    }
    const updateTeamServices = async(services) => {
        let didAdd = services.length > (team.talent_service_groups || []).length

        return teamRequest('updateTeamServices', services,
            `Team Project Type ${didAdd ? 'Added' : 'Removed'}`
        );
    }
    const updateTeamMembers = async(positions) => {
        return teamRequest('updateTeamMembers', positions, "Team Member Updated");
    }
    const updateTeamLeads = async(positions) => {
        return teamRequest('updateTeamLeads', positions);
    }

    const inviteTalentMember = async(talentUser, disciplines = []) => {
        const request = async() => await team.inviteTalentMember(talentUser).catch(console.warn)
        return teamRequest(request, null , 'Team Member Invite Sent');
    }

    const inviteExternalMember = async(email, disciplines = []) => {
        const request = async() => await team.inviteExternalMember(email).catch(console.warn)
        return teamRequest(request, null , 'Team Member Invite Sent');
    }

    const resendInvite = async(invite) => {
        return teamRequest('resendInvite', invite , 'Team Member Invite Resent');
    }
    const cancelInvite = async(member) => {
        return teamRequest('cancelInvite', member.invite_id , 'Team Member Invite Cancelled');
    }

    const grantTeamMemberAdminRights = async(member) => {
        return teamRequest('grantTeamMemberAdminRights', member, 'Team Member Updated');
    }
    const revokeTeamMemberAdminRights = async (member) => {
        return teamRequest('revokeTeamMemberAdminRights', member, 'Team Member Updated');
    }

    const leaveTeam = async () => {
        if (!auth.isTalent() && !auth.canManageClientResourceTalent()) {
            return TB.toast('You don\'t have permission to complete this action')
        }

        if (auth.isTalent() && team.is_owner) {
            return TB.toast('You must assign another team owner if you want to leave the team.')
        }

        const didConfirm = await TB.alert(
            'danger',
            'Leave Team',
            `Are you sure you want to leave the team: ${team.name}? This will remove you from any opportunities with this team. Note: this cannot be undone.`,
            [
                { title: 'Leave Team' },
                { title: 'Cancel' },
            ]
        )

        if (!didConfirm) {
            return;
        }

        await setIsUpdating(true);

        let response = await teamRequestMutation.mutateAsync(
            () => team.leaveTeam()
        ).catch(console.warn);

        if (!response) {
            setIsUpdating(false);
            TB.showServerErrorToast();
            return;
        }
        const type = auth.canManageClientResourceTalent() ? 'supplier' : 'talent';

        TB.toast('You have left the team', {type: 'info'})

        return navigate(`/${type}/teams/all`, {replace: true})
    }

    const deleteTeam = async () => {
        if (!auth.isTalent() && !auth.canManageClientResourceTalent()) {
            return TB.toast('You don\'t have permission to complete this action')
        }

        if (auth.isTalent() && !team.is_owner) {
            return TB.toast('You don\'t have permission to complete this action')
        }

        const didConfirm = await TB.alert(
            'danger',
            'Delete Team',
            `Are you sure you want to delete the team: ${team.name}? This will remove all team members and any existing opportunities from the team. Note: this cannot be undone.`,
            [
                { title: 'Delete Team' },
                { title: 'Cancel' },
            ]
        )

        if (!didConfirm) {
            return;
        }

        await setIsUpdating(true);

        let response = await teamRequestMutation.mutateAsync(
            () => team.deleteTeam()
        ).catch(console.warn);

        if (!response) {
            setIsUpdating(false);
            TB.showServerErrorToast();
            return;
        }
        const type = auth.canManageClientResourceTalent() ? 'supplier' : 'talent';

        TB.toast('Team successfully deleted', {type: 'info'})

        return navigate(`/${type}/teams/all`, {replace: true})
    }

    const authMember = useMemo(() => teamId && team && team.teamMemberPositionForAuth?.(auth), [teamId,auth, isUpdating, team, isFetching]);
    const isOwner = useMemo(() => auth.canManageTalent() ? true : (team?.is_owner || authMember?.role === 'Owner'), [team,authMember]);
    const isAdmin = useMemo(() => authMember?.role === 'Admin', [authMember]);

    const removeTeamMember = async(member) => {

        const authMember = team?.teamMemberPositionForAuth(auth);
        if (!auth.canManageTalent() && !auth.canManageClientResourceTalent() &&
            (!team.can_manage || (authMember.role !== 'Owner' && team.teamMemberHasAdminRights(member)))) {
            return TB.toast(
                'Unauthorized to Remove member',
                {'theme': 'danger'}
            )
        }
        const confirmRemoval = await TB.sysAlert('talent_teams.remove_member_confirm')
        if (!confirmRemoval) {
            return true;
        }
        return teamRequest('removeTeamMember', member , 'Team Member Removed');
    }

    const requestApprovalForTeamServices = async(services) => {
        return teamRequest('requestApprovalForTeamServices', services, auth.canManageTalent() ? 'Project Types Approved' : 'Request for Approval Sent');
    }
    const requestHelpFillingDisciplines = async(disciplines) => {
        let confirm = await TB.sysAlert('talent_teams.request_role_fill_confirm')

        if (confirm) {
            return teamRequest('requestHelpFillingDisciplines', disciplines, 'talent_teams.request_role_fill_success');
        }
    }
    const acceptTeamInvite = async() => {
        return teamRequest('acceptTeamInvite', {}, 'Team Invite Accepted');
    }
    const rejectTeamInvite = async() => {
        let didReject = await teamRequest('rejectTeamInvite', {}, 'Team Invite Declined');

        if (didReject) {
            await queryClient.invalidateQueries('fetchAllTeamsForTalent');
            return true;
        }
    }
    const addTalentResourceMember = async(user_id) => {
        return teamRequest('addTalentResourceMember', user_id, 'Team Member Added');
    }
    const removeTalentResourceMember = async(member) => {
        return this.removeTeamMember(member)
    }

    let value = {
        team,
        error,
        isLoading: isFetching || isInitializing,
        isUnavailable: !teamId,
        isUpdating,
        isAuthOwner: isOwner,
        isAuthAdmin: isAdmin,
        createTeam,
        onModelChange: (props) => {
            let updates = {...team};
            for (let [key,value] of Object.entries(props)) {
                updates = TB.set(updates, key, value)
            }
            setTeam(TalentTeam.make({...updates}))
        },
        resetModel: () => {
            if (!teamId) return;

            setTeam(TalentTeam.make({...teamResponse}))
        },
        teamResponse,
        cancelInvite,
        updateTeamDetails,
        updateTeamServices,
        updateTeamLeads,
        updateTeamMembers,
        inviteTalentMember,
        inviteExternalMember,
        requestApprovalForTeamServices,
        requestHelpFillingDisciplines,
        acceptTeamInvite,
        rejectTeamInvite,
        addTalentResourceMember,
        removeTalentResourceMember,
        resendInvite,
        grantTeamMemberAdminRights,
        revokeTeamMemberAdminRights,
        removeTeamMember,
        deleteTeam,
        leaveTeam,
        toggleMemberLead: (member) => {
            let members = TB.map([...team?.positions], position => {
                if (position.id === member.id) {
                    position.is_lead = !position.is_lead;
                }
                return position
            })
            return updateTeamLeads(members)
        }
    }

    return (
        <TalentTeamContext.Provider value={value}>
            {props.children}
        </TalentTeamContext.Provider>
    );
}

const useTalentTeam = () => {
    return React.useContext(TalentTeamContext);
}
export default TalentTeamProvider;
export {
    useTalentTeam,
    TalentTeamContext
}